import {
  activeWorkspaceIdAtom,
  selectedProcessIdAtom,
} from '../../atoms/workspace';
import { useProcesses } from '../../hooks/useProcesses';
import { useProcessViews } from '../../hooks/useProcessViews';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { AddProcessButton } from '../AddProcessButton';
import { ProcessCard } from './ProcessCard';
import {
  WorkspaceActionsMenu,
  type WorkspaceProcessStats,
} from './WorkspaceActionsMenu';
import { Center, Collapse, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { ChevronDown, ChevronLeft, ChevronRight, Timer } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type MutableWorkspaceProcessStats = {
  activeProcessCount: number;
  archivedProcessCount: number;
  snoozedProcessCount: number;
  totalProcessCount: number;
};

type ProcessListPanelProps = {
  readonly onBackToWorkspaces?: () => void;
  readonly onProcessSelect?: () => void;
  readonly showTimelineButton?: boolean;
};

const emptyStats: WorkspaceProcessStats = {
  activeProcessCount: 0,
  archivedProcessCount: 0,
  snoozedProcessCount: 0,
  totalProcessCount: 0,
};

export const ProcessListPanel = ({
  onBackToWorkspaces,
  onProcessSelect,
  showTimelineButton,
}: ProcessListPanelProps) => {
  const activeWorkspaceId = useAtomValue(activeWorkspaceIdAtom);
  const setSelectedProcessId = useSetAtom(selectedProcessIdAtom);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { processes: allProcesses } = useProcesses();
  const { destroy, rename, workspaces } = useWorkspaces();
  const activeWorkspace = workspaces.find((ws) => ws.id === activeWorkspaceId);

  const processStatsByWorkspace = useMemo(() => {
    const now = new Date();
    const stats = new Map<string, MutableWorkspaceProcessStats>();

    for (const process of allProcesses) {
      const counts = stats.get(process.workspaceId) ?? { ...emptyStats };
      counts.totalProcessCount += 1;

      if (process.archivedAt) {
        counts.archivedProcessCount += 1;
      } else if (process.snoozedUntil && new Date(process.snoozedUntil) > now) {
        counts.snoozedProcessCount += 1;
      } else {
        counts.activeProcessCount += 1;
      }

      stats.set(process.workspaceId, counts);
    }

    return stats;
  }, [allProcesses]);

  const processes = useProcessViews({
    mode: 'active',
    workspaceId: activeWorkspaceId,
  });
  const snoozedProcesses = useProcessViews({
    mode: 'snoozed',
    workspaceId: activeWorkspaceId,
  });

  // Auto-select first process when workspace changes or processes load (desktop only).
  // On mobile, auto-select would immediately flip to detail view, trapping the user.
  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    if (processes.length > 0) {
      setSelectedProcessId((current) => {
        const existsInList = processes.some((proc) => proc.id === current);
        if (!current || !existsInList) {
          return processes[0].id;
        }

        return current;
      });
    } else {
      setSelectedProcessId(null);
    }
  }, [isDesktop, processes, setSelectedProcessId]);

  if (!activeWorkspaceId) {
    return null;
  }

  return (
    <div className="h-full flex flex-col pt-2 pl-2 pb-2 pr-2 md:pr-0">
      {onBackToWorkspaces && activeWorkspace && (
        <UnstyledButton
          aria-label="Back to workspaces"
          className="flex items-center gap-1 text-sm text-blue-500 mb-2 px-1"
          onClick={onBackToWorkspaces}
        >
          <ChevronLeft size={16} />
          Workspaces
        </UnstyledButton>
      )}

      <div className="flex items-center justify-between px-1 pr-3 mb-2">
        <Text className="text-[10px] uppercase text-black/40 dark:text-white/40 font-semibold tracking-wider truncate">
          {activeWorkspace?.title ?? 'Workspace'}
        </Text>
        {activeWorkspace && activeWorkspace.position !== 0 && (
          <WorkspaceActionsMenu
            onDeleteWorkspace={destroy}
            onRenameWorkspace={rename}
            processStats={
              processStatsByWorkspace.get(activeWorkspace.id) ?? emptyStats
            }
            showTimelineButton={showTimelineButton}
            workspace={activeWorkspace}
            workspaces={workspaces}
          />
        )}
      </div>

      <div className="mb-2 pr-2">
        <AddProcessButton workspaceId={activeWorkspaceId} />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hidden">
        {processes.length === 0 ? (
          <Center className="pt-8">
            <Text className="text-xs text-black/30 dark:text-white/30">
              No active processes
            </Text>
          </Center>
        ) : (
          processes.map((process, index) => (
            <ProcessCard
              commentsCount={process.commentsCount}
              id={process.id}
              incompleteTodosCount={process.incompleteTodosCount}
              isPinned={process.isPinned}
              key={process.id}
              lastActivityAt={process.lastActivityAt}
              neglectRank={index}
              onSelect={onProcessSelect}
              title={process.title}
            />
          ))
        )}
      </div>

      {snoozedProcesses.length > 0 && (
        <>
          <UnstyledButton
            className="flex items-center gap-1 px-2 py-1.5 mt-1 text-xs text-black/40 dark:text-white/40 hover:text-black/60 dark:hover:text-white/60 w-full"
            onClick={() => setShowSnoozed((prev) => !prev)}
          >
            {showSnoozed ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )}
            <Timer size={12} />
            <span>{snoozedProcesses.length} snoozed</span>
          </UnstyledButton>
          <Collapse expanded={showSnoozed}>
            <div className="pb-1">
              {snoozedProcesses.map((process, index) => (
                <ProcessCard
                  commentsCount={process.commentsCount}
                  id={process.id}
                  incompleteTodosCount={process.incompleteTodosCount}
                  isPinned={process.isPinned}
                  key={process.id}
                  lastActivityAt={process.lastActivityAt}
                  neglectRank={index}
                  onSelect={onProcessSelect}
                  title={process.title}
                />
              ))}
            </div>
          </Collapse>
        </>
      )}
    </div>
  );
};
